<!-- apps/frontend/src/components/flow/NodeConfigModal.vue -->
<template>
  <!--
    closeOnEscape is off on purpose: UnifiedFlowEditor's useFlowKeyboard already
    closes this modal on Escape (onEscape clears configModalNodeId). Both would be
    document keydown listeners, so leaving it on would double-handle the key.
  -->
  <Dialog
    :visible="true"
    modal
    :draggable="false"
    dismissable-mask
    :close-on-escape="false"
    :style="{ width: '700px', maxHeight: '85vh' }"
    :pt="{ root: { class: 'rounded-lg' } }"
    @update:visible="(v) => { if (!v) emit('close') }"
  >
    <template #header>
      <div class="flex items-center gap-2">
        <h2 class="text-lg font-semibold capitalize">
          {{ $t('flowEditor.nodeConfigModal.titlePrefix') }} {{ getNodeTitle(node.type) }}
        </h2>
        <span
          v-if="node.data?.__isExitNode"
          class="material-icons text-green-400 text-base select-none"
          role="img"
          :aria-label="$t('flowEditor.nodeEditorPanel.exitNodeTooltip')"
          v-tooltip="$t('flowEditor.nodeEditorPanel.exitNodeTooltip')"
        >logout</span>
      </div>
    </template>

    <component
      :is="editorComponent"
      :node-data="localData"
      :has-error-edge="hasErrorEdge"
      :bot-id="botId"
      :flow-mode="flowMode"
      :flow-nodes="flowNodes"
      @update:node-data="localData = $event"
      @update="localData = $event"
      @open-modal="emit('open-code-editor')"
    />

    <template #footer>
      <Button
        severity="secondary"
        :label="$t('flowEditor.nodeConfigModal.cancelButton')"
        @click="emit('close')"
      />
      <Button
        :label="$t('flowEditor.nodeConfigModal.saveButton')"
        :disabled="!isDirty"
        @click="onSave"
      />
    </template>
  </Dialog>
</template>

<script setup>
/**
 * NodeConfigModal.vue
 *
 * Generic "Configure <node>" dialog of the flow editor: renders the editor
 * panel matching the node type on a local copy of the node data and emits it
 * on Save, which stays disabled until the data actually differs from what it
 * was when the dialog opened. Cancel, the × button and a click on the mask
 * close it without saving; Escape is handled by the parent (see the template
 * comment).
 * Visibility is driven by the parent's v-if, so the Dialog is always visible
 * while mounted.
 *
 * @example
 * <NodeConfigModal
 *   v-if="configModalNodeId && configModalNode?.type !== 'executeCode'"
 *   :node="node"
 *   :has-error-edge="hasErrorEdge"
 *   :bot-id="botId"
 *   :flow-mode="config.mode"
 *   @save="onModalSave"
 *   @close="configModalNodeId = null"
 *   @open-code-editor="isCodeEditorModalOpen = true"
 * />
 */
import { ref, computed, defineAsyncComponent } from 'vue';
import { useI18n } from 'vue-i18n';
import { stripInternalKeys } from '../../utils/flowExitNodes';

const { t } = useI18n();

const props = defineProps({
  /** The flow node being configured ({ id, type, data }) */
  node: { type: Object, required: true },
  // Passata al pannello del nodo: alcuni editor descrivono un comportamento che
  // dipende da come il nodo e' collegato sul canvas (vedi ApiCallEditor).
  hasErrorEdge: { type: Boolean, default: false },
  /** Current bot id, forwarded to the editor panel */
  botId: { type: String, default: null },
  /** Flow mode (config.mode of the editor), forwarded to the editor panel */
  flowMode: { type: String, default: null },
  /** Canvas nodes, forwarded to the panel (variable suggestions of ifCondition) */
  flowNodes: { type: Array, default: () => [] },
});

/**
 * @event save - User clicked Save; payload is a copy of the edited node data
 * @event close - User cancelled (Cancel button, × or mask click)
 * @event open-code-editor - The panel asked to open the code editor modal
 */
const emit = defineEmits(['save', 'close', 'open-code-editor']);

const localData = ref({ ...props.node.data });

// Dirty check for the Save button. The baseline is a deep JSON snapshot taken
// once at setup (the parent's v-if remounts this component on every open), not
// a live comparison with props.node.data: localData is only a shallow copy and
// some panels (e.g. ApiCallEditor's auth/headers/queryParams) mutate nested
// objects in place, which are shared with the canvas node, so a live comparison
// would never see those edits. Editor-only keys (__isExitNode,
// __validationError) are stripped on both sides: they are not user edits.
const baseline = JSON.stringify(stripInternalKeys(props.node.data));
const isDirty = computed(() => JSON.stringify(stripInternalKeys(localData.value)) !== baseline);

const editors = {
  input: defineAsyncComponent(() => import('./panels/StartNodeEditor.vue')),
  botSays: defineAsyncComponent(() => import('./panels/BotSaysEditor.vue')),
  botAsks: defineAsyncComponent(() => import('./panels/BotAsksEditor.vue')),
  condition: defineAsyncComponent(() => import('./panels/ConditionEditor.vue')),
  ifCondition: defineAsyncComponent(() => import('./panels/IfConditionEditor.vue')),
  setVariable: defineAsyncComponent(() => import('./panels/SetVariableEditor.vue')),
  addTags:      defineAsyncComponent(() => import('./panels/AddTagsEditor.vue')),
  executeCode: defineAsyncComponent(() => import('./panels/ExecuteCodeEditor.vue')),
  apiCall: defineAsyncComponent(() => import('./panels/ApiCallEditor.vue')),
  webSearch: defineAsyncComponent(() => import('./panels/WebSearchEditor.vue')),
  intentVerifier: defineAsyncComponent(() => import('./panels/IntentVerifierEditor.vue')),
  useMcpTool: defineAsyncComponent(() => import('./panels/UseMcpToolEditor.vue')),
  botRespondWithKb: defineAsyncComponent(() => import('./panels/BotRespondWithKbEditor.vue')),
  toolInput:  defineAsyncComponent(() => import('./panels/ToolInputEditor.vue')),
  toolOutput: defineAsyncComponent(() => import('./panels/ToolOutputEditor.vue')),
  sendMessage:             defineAsyncComponent(() => import('./panels/SendMessageEditor.vue')),
  sendChatMessage:         defineAsyncComponent(() => import('./panels/SendChatMessageEditor.vue')),
  waitForUserReply:        defineAsyncComponent(() => import('./panels/WaitForUserReplyEditor.vue')),
  triggerAgent:            defineAsyncComponent(() => import('./panels/TriggerAgentEditor.vue')),
  askAgent:                defineAsyncComponent(() => import('./panels/AskAgentEditor.vue')),
  sendWhatsapp:            defineAsyncComponent(() => import('./panels/SendWhatsappEditor.vue')),
  sendWhatsappTemplate:    defineAsyncComponent(() => import('./panels/SendWhatsappTemplateEditor.vue')),
  activateTransfer:   defineAsyncComponent(() => import('./panels/ActivateTransferEditor.vue')),
  deactivateTransfer: defineAsyncComponent(() => import('./panels/DeactivateTransferEditor.vue')),
  transferCall:       defineAsyncComponent(() => import('./panels/TransferCallEditor.vue')),
  endCall:            defineAsyncComponent(() => import('./panels/EndCallEditor.vue')),
  fetchHistory:       defineAsyncComponent(() => import('./panels/FetchHistoryEditor.vue')),
  webhookInput:       defineAsyncComponent(() => import('./panels/WebhookInputEditor.vue')),
  webhookOutput:      defineAsyncComponent(() => import('./panels/WebhookOutputEditor.vue')),
};

const editorComponent = computed(() => editors[props.node.type] || null);

const getNodeTitle = (type) => {
  if (type === 'input') return t('flowEditor.nodeEditorPanel.titleStart');
  return t(`flowEditor.palette.${type}`, type);
};

const onSave = () => {
  emit('save', { ...localData.value });
};
</script>
